import { dineOpsService } from 'api/services/dineOpsService';
import type { DineItem } from 'api/types';

/**
 * Extras for an add-on picker (DO-315, spec 2.42 §8.6). An add-on is an item
 * picked from the menu, usually from the Extras category, which is listed
 * first. A dish with sizes is offered by its sizes ("Dip (Large)"), never by
 * itself, because the add-on must name one price.
 */
export interface ExtraOption {
  value: string;
  label: string;
  itemName: string;
  price: number;
}

const isExtras = (i: DineItem) => (i.courseName ?? '').trim().toLowerCase() === 'extras';

export async function loadExtraOptions(excludeItemId?: string | null): Promise<ExtraOption[]> {
  const page = await dineOpsService.getItems({ page: 1, pageSize: 500 });
  const items = (page.data ?? [])
    .filter((i) => i.id !== excludeItemId && !i.isOpenPrice && i.isActive !== false)
    .sort((a, b) => Number(isExtras(b)) - Number(isExtras(a)) || a.itemName.localeCompare(b.itemName));

  const options: ExtraOption[] = [];
  for (const i of items) {
    const where = isExtras(i) ? 'Extras' : i.courseName;
    if ((i.variantCount ?? 0) > 0) {
      const sizes = await dineOpsService.getVariants(i.id).catch(() => []);
      sizes.forEach((s) => options.push({
        value: s.id, itemName: `${i.itemName} (${s.variantName})`, price: Number(s.price),
        label: `${i.itemName} (${s.variantName}) · ${s.itemCode} · ${Number(s.price).toFixed(2)} · ${where}`,
      }));
    } else {
      options.push({
        value: i.id, itemName: i.itemName, price: Number(i.price),
        label: `${i.itemName} · ${i.itemCode} · ${Number(i.price).toFixed(2)} · ${where}`,
      });
    }
  }
  return options;
}
